import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { BbIcon } from './BbIcon'

/* The field picker for the mapping grid.

   A native <select> cannot be searched, cannot show which file column already
   feeds a field, and renders its options in the OS's own style, so this is a
   combobox: a trigger showing the chosen field, and a panel with a search box
   over the grouped list.

   The panel repeats the entity's sections rather than one flat run of names.
   The section titles are decided by the page — they differ per entity — and
   arrive on each option as `group`. */

/** One selectable QuickBooks field. */
export interface PickOption {
  /** The registry name the mapping stores ("Line.ItemRef"). */
  value: string
  /** What the user reads. Not the export wording: "Invoice No.", not "RefNumber". */
  label: string
  /** The API path, searched alongside the label. */
  name: string
  /** The section's title, e.g. "Line Items" — decided per entity by the server. */
  group: string
  /** One line about what the section is for, when the server supplies one. */
  groupNote: string | null
  required: boolean
  /** The file column already feeding this field, when one is. */
  takenBy: string | null
  /** What the field is for, in plain words. */
  help: string | null
  /** What QuickBooks expects of the value: kind, length, scope. */
  meta: string
}

/* What the info icon explains. Rendered into a portal rather than beside the
   row: the panel clips its own overflow and the list scrolls, so a tooltip
   inside either would be cut off at the edges. */
function InfoTip({ text, swallowClick }: { text: PickOption; swallowClick?: boolean }) {
  const [at, setAt] = useState<{
    side: 'left' | 'right'
    left?: number
    right?: number
    top: number
  } | null>(null)
  const box = useRef<HTMLDivElement>(null)

  const WIDTH = 320
  const GAP = 12

  /* Beside the icon, on whichever side of it has room. The vertical position is
     the icon's centre; the box is nudged afterwards if that would put it off the
     top or bottom of the window. */
  const place = (node: HTMLElement) => {
    const rect = node.getBoundingClientRect()
    const side: 'left' | 'right' = rect.left > WIDTH + GAP + 8 ? 'left' : 'right'
    setAt({
      side,
      // Anchored by the edge that faces the icon.
      ...(side === 'left'
        ? { right: window.innerWidth - rect.left + GAP }
        : { left: rect.right + GAP }),
      top: rect.top + rect.height / 2,
    })
  }

  /* The box is centred on the icon, so its own height decides whether that puts
     it off the top or bottom; `--bb-tip-arrow` carries back how far it had to
     move, so the arrow still points at the icon rather than at the box's middle.
     Written to the node rather than to state because the height is only knowable
     after the first paint. */
  useLayoutEffect(() => {
    const node = box.current
    if (!at || !node) return
    const height = node.offsetHeight
    const half = height / 2
    const top = Math.min(Math.max(at.top, half + 8), window.innerHeight - half - 8)
    node.style.top = `${top}px`
    const arrow = Math.min(Math.max(at.top - (top - half), 14), height - 14)
    node.style.setProperty('--bb-tip-arrow', `${arrow}px`)
  }, [at])

  useEffect(() => {
    if (!at) return
    // Scrolling moves the row out from under the box, so the box goes with it
    // rather than hanging over whichever field has slid into its place.
    const hide = () => setAt(null)
    document.addEventListener('scroll', hide, true)
    return () => document.removeEventListener('scroll', hide, true)
  }, [at])

  return (
    <span
      className="bb-tip-btn"
      role="img"
      aria-label={`About ${text.label}`}
      onMouseEnter={(event) => place(event.currentTarget)}
      onMouseLeave={() => setAt(null)}
      onClick={(event) => {
        // Reading about a field must not also pick it.
        if (swallowClick) event.stopPropagation()
      }}
    >
      <BbIcon name="info" />
      {at
        ? createPortal(
            // The `bb` class carries the design tokens. The box is portalled onto
            // document.body, outside the page's own `.bb` wrapper, so without it
            // every `--bb-*` value here is undefined and the box draws transparent.
            <div
              className={`bb bb-tip from-${at.side}`}
              role="tooltip"
              ref={box}
              style={{ left: at.left, right: at.right, top: at.top }}
            >
              <div className="bb-tip-h">
                {text.label}
                {text.required ? <span className="bb-tip-badge">Required</span> : null}
              </div>
              <div className="bb-tip-k">{text.meta}</div>
              {text.help ? <p className="bb-tip-b">{text.help}</p> : null}
              {text.groupNote ? (
                <p className="bb-tip-b bb-tip-s">
                  <span className="bb-tip-s-h">{text.group}</span>
                  {text.groupNote}
                </p>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </span>
  )
}

export function BbFieldPicker({
  id,
  value,
  options,
  onChange,
  currentColumn,
  ignoreLabel,
  ariaLabel,
}: {
  id: string
  value: string | null
  options: PickOption[]
  onChange: (field: string | null) => void
  /** The row's own file column, so its own mapping is not shown as a conflict. */
  currentColumn: string
  ignoreLabel: string
  ariaLabel: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const [up, setUp] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const search = useRef<HTMLInputElement>(null)

  const selected = options.find((option) => option.value === value) ?? null

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return options
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) ||
        option.name.toLowerCase().includes(needle) ||
        option.group.toLowerCase().includes(needle),
    )
  }, [options, query])

  // Index 0 is the "ignore" row; the options follow it.
  const rows: (PickOption | null)[] = [
    ...(query.trim() ? [] : [null]),
    ...matches,
  ]

  /* Opening measures the trigger: a panel near the bottom of the window opens
     upward instead of running off the page, which is what makes the last rows
     usable. It also starts the highlight at the top of a fresh list. */
  useEffect(() => {
    if (!open) return
    const box = root.current?.getBoundingClientRect()
    // oxlint-disable-next-line react/set-state-in-effect
    if (box) setUp(window.innerHeight - box.bottom < 320)
    // oxlint-disable-next-line react/set-state-in-effect
    setActive(0)
    search.current?.focus()

    const onDown = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const choose = (option: PickOption | null) => {
    onChange(option ? option.value : null)
    setOpen(false)
    setQuery('')
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const step = event.key === 'ArrowDown' ? 1 : -1
      setActive((index) => Math.min(rows.length - 1, Math.max(0, index + step)))
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      if (rows[active] !== undefined) choose(rows[active])
      return
    }
    if (event.key === 'Escape') setOpen(false)
    if (event.key === 'Tab') setOpen(false)
  }

  return (
    <div className="bb-pick" ref={root}>
      <button
        className={selected ? 'bb-pick-btn' : 'bb-pick-btn bb-pick-off'}
        type="button"
        id={id}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {selected ? (
          <>
            <BbIcon name="check-circle" />
            <span className="bb-pick-label">{selected.label}</span>
            <InfoTip text={selected} />
            {selected.required ? <span className="bb-pick-badge">Required</span> : null}
            <span className="bb-pick-group">{selected.group}</span>
          </>
        ) : (
          <span className="bb-pick-none">{ignoreLabel}</span>
        )}
        <BbIcon name="chevron-down" />
      </button>

      {open ? (
        <div className={up ? 'bb-pick-panel up' : 'bb-pick-panel'}>
          <div className="bb-pick-search">
            <BbIcon name="search" />
            <input
              ref={search}
              type="search"
              autoComplete="off"
              placeholder="Search fields…"
              aria-label="Search QuickBooks fields"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                // The list changes under the cursor, so the highlight returns to
                // the top rather than pointing at whatever now sits at the index.
                setActive(0)
              }}
              onKeyDown={onKeyDown}
            />
          </div>

          <ul className="bb-pick-list" role="listbox" aria-label={ariaLabel}>
            {/* The "ignore" choice is offered only when nothing has been typed:
                while filtering, it would sit above the results and push them
                down for a row the search was not about. */}
            {query.trim() ? null : (
              <li key="__none__">
                <div
                  role="option"
                  aria-selected={value === null}
                  className={active === 0 ? 'bb-pick-opt is-active' : 'bb-pick-opt'}
                  onMouseEnter={() => setActive(0)}
                  onClick={() => choose(null)}
                >
                  <span className="bb-pick-opt-label bb-pick-opt-muted">{ignoreLabel}</span>
                </div>
              </li>
            )}

            {matches.map((option, position) => {
              // Its own mapping is not a conflict; another column's is.
              const takenBy =
                option.takenBy && option.takenBy !== currentColumn ? option.takenBy : null
              const chosen = option.value === value
              // The keyboard index `rows` carries: the ignore row is index 0 when
              // it is shown, and every option follows it.
              const index = (query.trim() ? 0 : 1) + position

              // A section is introduced once, by its own heading, rather than by
              // repeating its name on every row inside it.
              const startsSection =
                position === 0 || matches[position - 1].group !== option.group

              return (
                <Fragment key={option.value}>
                  {startsSection ? (
                    <li className="bb-pick-sec" aria-hidden="true">
                      <span className="bb-pick-sec-h">{option.group}</span>
                      {option.groupNote ? (
                        <span className="bb-pick-sec-n">{option.groupNote}</span>
                      ) : null}
                    </li>
                  ) : null}
                  <li>
                    {/* A div rather than a button: the row carries the info icon,
                        and a nested control inside a button is neither valid nor
                        clickable. The search box keeps the keyboard. */}
                    <div
                      role="option"
                      aria-selected={chosen}
                      className={`bb-pick-opt${index === active ? ' is-active' : ''}${
                        chosen ? ' is-chosen' : ''
                      }`}
                      onMouseEnter={() => setActive(index)}
                      onClick={() => choose(option)}
                    >
                      <span className="bb-pick-opt-label">
                        {option.label}
                        {option.required ? <span className="bb-req"> *</span> : null}
                      </span>
                      <InfoTip text={option} swallowClick />
                      {takenBy ? (
                        <span className="bb-pick-taken" title={`Already fed by “${takenBy}”`}>
                          {takenBy}
                        </span>
                      ) : null}
                    </div>
                  </li>
                </Fragment>
              )
            })}
          </ul>

          {matches.length === 0 ? (
            <p className="bb-pick-empty">No field matches “{query}”.</p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
